// src/components/Post.tsx
'use client'

import { PortableText } from '@portabletext/react'
import { CHANGELOG_QUERYResult, POST_QUERYResult } from '../../sanity.types'
import { portableTextComponents } from './sanity/PortableTextComponents'
import { GoBack } from './BlogLayout'
import { PostContainer } from './marketing/Containers'
import { ProductBanner } from './ProductBanner'
import { SubscribeFooter } from './SubscribeFooter'
import { blogConvertKitFormId, changelogConvertKitFormId } from '@/config'
import { AuthorsFull, AuthorsMini } from './sanity/AuthorsFull'
import clsx from 'clsx'
import { formatDate } from '@/utils/formatDate'
import TableOfContents from './sanity/TableOfContents'
import { Prose } from './Prose'

export function Post({
  post,
  source,
}: {
  post: POST_QUERYResult | CHANGELOG_QUERYResult
  source: 'posts' | 'changelog'
}) {
  const {
    title,
    body,
    authors,
    publishedAt,
    hideProductBanner,
    hideAuthorCard,
    hideCTA,
    hideTableOfContents,
  } = post || {}

  return (
    <div className="mx-auto max-w-[1500px]">
      {source === 'posts' ? (
        <GoBack href="/posts" text="All posts" />
      ) : (
        <GoBack href="/changelog" text="View full changelog" />
      )}

      <PostContainer className={'max-w-8xl'}>
        <div className="min-w-0 max-w-5xl flex-auto px-4 py-0 lg:pl-8 lg:pr-0 xl:px-14">
          <Prose className="relative !w-full pt-10 font-sans">
            <header className="mb-9 w-full">
              {title ? (
                <h1
                  className={clsx(
                    '!mb-[24px] !font-sans text-3xl !font-bold tracking-[-0.8px] !text-[#e2e8f0]'
                  )}
                >
                  {title}
                </h1>
              ) : null}

              {publishedAt && (
                <div className="text-sm leading-6">
                  <dl>
                    <dt className="sr-only">Date</dt>
                    <dd
                      className={clsx(
                        'absolute inset-x-0 -top-[7px] flex items-start !p-0 text-slate-400'
                      )}
                    >
                      <time
                        dateTime={publishedAt}
                        suppressHydrationWarning={true}
                        className=""
                      >
                        {formatDate(publishedAt, '{dddd}, {MMMM} {DD}, {YYYY}')}
                      </time>
                    </dd>
                  </dl>
                </div>
              )}
              {authors && (
                <div className="mt-0">
                  <AuthorsMini authors={authors || null} />
                </div>
              )}
            </header>

            {body ? (
              <PortableText value={body} components={portableTextComponents} />
            ) : null}
          </Prose>

          {!hideAuthorCard && <AuthorsFull authors={authors || []} />}
        </div>

        {!hideTableOfContents && (
          <TableOfContents headings={post?.headings} showCTA={!hideCTA} />
        )}
      </PostContainer>

      <PostContainer className="max-w-8xl overflow-hidden">
        <div className="min-w-0 flex-auto px-4 py-0 lg:pl-8 lg:pr-0 xl:px-14">
          {!hideProductBanner && <ProductBanner />}

          {source === 'posts' ? (
            <SubscribeFooter
              title="Want to hear about our latest Snowflake learnings?"
              convertKitFormID={blogConvertKitFormId}
            />
          ) : (
            <SubscribeFooter
              title="Want to get notified when we release new features?"
              convertKitFormID={changelogConvertKitFormId}
            />
          )}
        </div>
      </PostContainer>
    </div>
  )
}
